"use client";

import { useEffect, useState } from "react";
import FeedPageClientMobile from "@/components/feed/feed-page-client-mobile";
import FeedPageClientDesktop from "@/components/feed/feed-page-client-desktop";
import { FeedDiscoverCategorySlider } from "@/components/feed/feed-discover-category-slider";
import { useOverlay } from "@/components/context/overlay-context";
import type { FeedPageClientProps } from "@/components/feed/feed-page-client-types";

function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const listener = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    media.addEventListener("change", listener);
    setIsDesktop(media.matches);
    return () => {
      media.removeEventListener("change", listener);
    };
  }, [breakpoint]);

  return isDesktop;
}

// Spec §E 1.6a: the discover-category slider used to be mounted once per
// breakpoint client (feed-page-client-{desktop,mobile}.tsx), each with its
// own local `discoverCategoryOpen`/`discoverBrandSelection` useState — a
// literal duplication of the SAME mechanism, not two different ones (both
// instances passed identical props and had identical open/close callbacks;
// see spec's 1.6a implementation notes for the full symmetry check). Single
// mount here, ABOVE the desktop/mobile swap, driven by the shared overlay
// panel slot (`{ kind: "discover-category", selection }`) — the slider now
// also survives 768px breakpoint crossings while open, which it did not
// before (each breakpoint client fully unmounted the other's slider).
export default function FeedPageClient(props: FeedPageClientProps) {
  const isDesktop = useIsDesktop();
  const { overlay, closePanel } = useOverlay();
  const discoverSelection = overlay.panel.kind === "discover-category" ? overlay.panel.selection : null;

  if (isDesktop === null) {
    return null;
  }
  return (
    <>
      {isDesktop ? (
        <FeedPageClientDesktop {...props} />
      ) : (
        <FeedPageClientMobile {...props} />
      )}
      <FeedDiscoverCategorySlider
        open={overlay.panel.kind === "discover-category"}
        onOpenChangeAction={(open) => {
          if (!open) closePanel();
        }}
        selection={discoverSelection}
        currentUserId={props.currentUserId}
      />
    </>
  );
}

